import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDebounced } from '@helpers/useDebounced';
import {
  AssetVersionCommentGroupMentionDto,
  AssetVersionCommentMentionDto
} from '@api/Api';

type MentionsParams = {
  assetVersionId?: string;
  getPotentialMentions: (args: {
    assetVersionId: string;
    searchQuery: string;
  }) => Promise<AssetVersionCommentMentionDto[]>;
  getPotentialGroupMentions: (args: {
    assetVersionId: string;
    searchQuery: string;
  }) => Promise<AssetVersionCommentGroupMentionDto[]>;
};

export function useCommentMentions({
  assetVersionId,
  getPotentialMentions,
  getPotentialGroupMentions
}: MentionsParams) {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const debouncedSearchQuery = useDebounced(searchQuery, 300);

  const potentialMentionsQuery = useQuery({
    queryKey: [
      'asset',
      'comment',
      'potential-mentions',
      assetVersionId,
      debouncedSearchQuery
    ],
    enabled: !!assetVersionId,
    keepPreviousData: true,
    queryFn: () =>
      getPotentialMentions({
        assetVersionId: assetVersionId || '',
        searchQuery
      })
  });

  const potentialGroupMentionsQuery = useQuery({
    queryKey: [
      'asset',
      'comment',
      'potential-group-mentions',
      assetVersionId,
      debouncedSearchQuery
    ],
    enabled: !!assetVersionId,
    keepPreviousData: true,
    queryFn: () =>
      getPotentialGroupMentions({
        assetVersionId: assetVersionId || '',
        searchQuery
      })
  });

  return {
    potentialMentions: useMemo(
      () => potentialMentionsQuery.data ?? [],
      [potentialMentionsQuery.data]
    ),
    potentialGroupMentions: useMemo(
      () => potentialGroupMentionsQuery.data ?? [],
      [potentialGroupMentionsQuery.data]
    ),
    setSearchQuery
  };
}
